import { Head, router, usePage } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useEffect, useState } from "react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil, parseLocalDate } from "@/lib/date-utils"
import { availabilityPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface CalendarDay {
  date: string
  day: number
  month_name: string
  in_month: boolean
  status: number
  notes: string | null
}

interface EmployeeOption {
  id: number
  name: string
}

interface Props {
  calendar_days: CalendarDay[]
  year: number
  month: number
  month_name: string
  selected_user: { id: number; name: string }
  employees: EmployeeOption[]
  permissions: {
    can_manage_others: boolean
    can_toggle_self: boolean
  }
  target_date?: string | null
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Availability", href: "/availability" },
]

const DAY_HEADERS = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
]

const STATUS_AVAILABLE = 0
const STATUS_UNAVAILABLE = 10

export default function Index({
  calendar_days,
  year,
  month,
  month_name,
  selected_user,
  employees,
  permissions,
  target_date,
}: Props) {
  const { auth } = usePage<{ auth: { user: { id: number } } }>().props
  const currentUserId = auth.user.id

  const [selectedEmployee, setSelectedEmployee] = useState(selected_user.id)
  const [dateInput, setDateInput] = useState("")
  const [modalOpen, setModalOpen] = useState(false)
  const [modalDate, setModalDate] = useState("")
  const [modalNotes, setModalNotes] = useState("")

  const canToggle =
    permissions.can_manage_others ||
    (permissions.can_toggle_self && selectedEmployee === currentUserId)

  const navigate = (newYear: number, newMonth: number, userId?: number) => {
    router.get(
      availabilityPath(),
      { year: newYear, month: newMonth, user_id: userId ?? selectedEmployee },
      { preserveState: true },
    )
  }

  const handlePrevMonth = () => {
    const prev =
      month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 }
    navigate(prev.year, prev.month)
  }

  const handleNextMonth = () => {
    const next =
      month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 }
    navigate(next.year, next.month)
  }

  const handleEmployeeChange = (empId: number) => {
    setSelectedEmployee(empId)
    navigate(year, month, empId)
  }

  const isFutureDate = (dateStr: string) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const date = parseLocalDate(dateStr)
    return date >= today
  }

  const postToggle = (
    date: string,
    status: number,
    notes: string | null = null,
  ) => {
    router.post("/availability/toggle", {
      availability: {
        user_id: selectedEmployee,
        date: date,
        status: status,
        notes: notes ?? "",
      },
    })
  }

  const handleDayClick = (day: CalendarDay) => {
    processDayToggle(day)
  }

  const handleModalSave = () => {
    postToggle(modalDate, STATUS_UNAVAILABLE, modalNotes)
    setModalOpen(false)
  }

  const processDayToggle = (day: CalendarDay) => {
    if (!day.in_month || !canToggle || !isFutureDate(day.date)) return
    if (day.status === STATUS_UNAVAILABLE) {
      postToggle(day.date, STATUS_AVAILABLE)
    } else {
      setModalDate(day.date)
      setModalNotes("")
      setModalOpen(true)
    }
  }

  const handleUpdate = () => {
    if (!dateInput) return
    const parsed = parseLocalDate(dateInput)
    if (isNaN(parsed.getTime())) return

    const d = parsed
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

    setDateInput("")

    // If the date is not in the currently displayed month, navigate there with target_date
    if (parsed.getFullYear() !== year || parsed.getMonth() + 1 !== month) {
      router.get(
        availabilityPath(),
        {
          year: parsed.getFullYear(),
          month: parsed.getMonth() + 1,
          user_id: selectedEmployee,
          target_date: dateStr,
        },
        { preserveState: true },
      )
      return
    }

    // Find the day in the current calendar
    const day = calendar_days.find((d) => d.date === dateStr)
    if (day) processDayToggle(day)
  }

  // Auto-process target_date from cross-month navigation
  useEffect(() => {
    if (!target_date) return
    const day = calendar_days.find((d) => d.date === target_date)
    if (day) processDayToggle(day)
  }, [target_date]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatModalDate = (dateStr: string) => {
    return formatDateUtil(dateStr, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  // Build weeks (rows of 7 days)
  const weeks: CalendarDay[][] = []
  for (let i = 0; i < calendar_days.length; i += 7) {
    weeks.push(calendar_days.slice(i, i + 7))
  }

  const dayCellClass = (day: CalendarDay) => {
    if (!day.in_month) return "bg-hpi-pale-gray"
    if (
      day.status === STATUS_UNAVAILABLE ||
      (day.notes && day.notes.length > 0)
    )
      return "bg-hpi-light-red"
    return "bg-hpi-light-green"
  }

  const isClickable = (day: CalendarDay) =>
    day.in_month && canToggle && isFutureDate(day.date)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Availability" />

      <div className="flex h-full flex-col">
        {/* Top controls bar */}
        <div className="border-hpi-light-gray flex items-center gap-3 border-b bg-white px-6 py-3">
          {permissions.can_manage_others ? (
            <select
              value={selectedEmployee}
              onChange={(e) => handleEmployeeChange(parseInt(e.target.value))}
              className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
            >
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-hpi-dark-gray text-sm font-medium">
              {selected_user.name}
            </span>
          )}
          <input
            type="date"
            value={dateInput}
            onChange={(e) => setDateInput(e.target.value)}
            className="border-hpi-light-gray w-40 rounded border px-3 py-1.5 text-sm"
          />
          <button
            onClick={handleUpdate}
            className="bg-hpi-blue hover:bg-hpi-dark-blue rounded px-4 py-1.5 text-sm text-white transition-colors"
          >
            Update
          </button>
        </div>

        {/* Month navigation */}
        <div className="flex items-center justify-center gap-4 bg-white px-6 py-3">
          <button
            onClick={handlePrevMonth}
            className="text-hpi-dark-gray hover:text-hpi-active-blue transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="text-hpi-dark-blue text-lg font-semibold tracking-wide">
            {month_name} {year}
          </h2>
          <button
            onClick={handleNextMonth}
            className="text-hpi-dark-gray hover:text-hpi-active-blue transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Calendar grid */}
        <div className="flex-1 overflow-auto px-6 pb-6">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {DAY_HEADERS.map((day) => (
                  <th
                    key={day}
                    className="bg-hpi-blue px-2 py-2 text-center text-xs font-semibold tracking-wider text-white"
                  >
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, weekIdx) => (
                <tr key={weekIdx}>
                  {week.map((day) => (
                    <td
                      key={day.date}
                      onClick={() => handleDayClick(day)}
                      className={`border-hpi-light-gray border p-2 text-center align-top ${dayCellClass(day)} ${
                        isClickable(day)
                          ? "cursor-pointer hover:opacity-80"
                          : ""
                      }`}
                    >
                      <div className="text-hpi-med-gray text-[10px] tracking-wider uppercase">
                        {day.month_name}
                      </div>
                      <div
                        className={`text-3xl font-light ${
                          day.in_month
                            ? "text-hpi-dark-blue"
                            : "text-hpi-med-gray"
                        }`}
                      >
                        {day.day}
                      </div>
                      {day.notes && (
                        <div className="text-hpi-dark-gray mt-1 text-xs leading-tight">
                          {day.notes}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Unavailable</DialogTitle>
            <DialogDescription>{formatModalDate(modalDate)}</DialogDescription>
          </DialogHeader>
          <div>
            <textarea
              value={modalNotes}
              onChange={(e) => setModalNotes(e.target.value)}
              placeholder="Notes (optional)"
              rows={3}
              className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
            />
          </div>
          <DialogFooter>
            <button
              onClick={() => setModalOpen(false)}
              className="border-hpi-light-gray rounded border px-4 py-1.5 text-sm transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleModalSave}
              className="bg-hpi-blue hover:bg-hpi-dark-blue rounded px-4 py-1.5 text-sm text-white transition-colors"
            >
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
